"use client";

import clsx from "clsx";
import { forwardRef, useRef, useState } from "react";
import { Icon } from "../Icon";
import { Loading } from "../Loading";
import { AnimatePresence, motion } from "framer-motion";

export type SearchInputProps = {
  block?: boolean;
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
  id?: string;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  value?: string;
  defaultValue?: string;
  autoFocus?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear?: () => void;
};

const styles = {
  base: "antialiased inline-flex bg-white disabled:bg-gray-400 font-semibold text-gray-1200 placeholder:text-gray-1100 border border-default hover:border-heavy whitespace-nowrap outline-none focus:ring-2 focus:ring-theme-accent ring-offset-1 transition duration-150 ease-in-out",
  size: {
    sm: "h-9 pl-9 pr-3 rounded-md",
    md: "h-10 pl-10 pr-4 rounded-md",
    lg: "h-12 pl-12 pr-4 rounded-md",
  },
  block: "flex w-full",
};

const iconContainerStyles = {
  base: "flex flex-shrink-0 items-center justify-center absolute left-0 top-0 bottom-0 pointer-events-none",
  size: {
    sm: "h-9 w-9",
    md: "h-10 w-10",
    lg: "h-12 w-12",
  },
};

const clearContainerStyles = {
  base: "flex flex-shrink-0 items-center justify-center absolute right-0 top-0 bottom-0 pointer-events-none",
  size: {
    sm: "h-9 w-9",
    md: "h-10 w-10",
    lg: "h-12 w-12",
  },
};

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      block,
      size = "md",
      isLoading,
      id,
      name,
      placeholder,
      disabled,
      value,
      defaultValue,
      autoFocus,
      onChange,
      onClear,
    },
    ref,
  ) => {
    const isControlled = value !== undefined;
    const internalRef = useRef<HTMLInputElement>(null);
    const inputRef = (ref || internalRef) as React.RefObject<HTMLInputElement>;
    const [internalValue, setInternalValue] = useState(defaultValue || "");

    const currentValue = isControlled ? value : internalValue;
    const hasValue = !!currentValue;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) setInternalValue(e.target.value);
      onChange?.(e);
    };

    const handleClear = () => {
      if (isControlled) return onClear?.();

      if (inputRef.current) {
        inputRef.current.value = "";
        setInternalValue("");
        // Create a synthetic event for onChange
        const syntheticEvent = {
          target: inputRef.current,
          currentTarget: inputRef.current,
        } as React.ChangeEvent<HTMLInputElement>;

        onChange?.(syntheticEvent);
      }
      onClear?.();
    };

    return (
      <div className={clsx([block && styles.block, "flex-col"])}>
        <div className="relative inline-flex">
          <div
            className={clsx(
              iconContainerStyles.base,
              iconContainerStyles.size[size],
            )}
          >
            <AnimatePresence mode="popLayout" initial={false}>
              {isLoading ? (
                <motion.div
                  className="flex items-center justify-center"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  key="loading"
                >
                  <Loading size="sm" />
                </motion.div>
              ) : (
                <motion.div
                  className="flex items-center justify-center"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  key="icon"
                >
                  <Icon icon="MagnifyingGlassSolid" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <input
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
            type="text"
            id={id ?? name}
            name={name}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            className={clsx([
              styles.base,
              styles.size[size],
              block && styles.block,
            ])}
            ref={inputRef}
          />
          <div
            className={clsx(
              clearContainerStyles.base,
              clearContainerStyles.size[size],
            )}
          >
            <AnimatePresence initial={false}>
              {hasValue && (
                <motion.button
                  type="button"
                  onClick={handleClear}
                  className="flex items-center justify-center h-5 w-5 hover:bg-gray-300 rounded-full outline-none focus-visible:inset-ring-2 inset-ring-green-600 transition cursor-pointer pointer-events-auto"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.125 }}
                  key="clear"
                  aria-label="Clear search"
                >
                  <Icon className="text-gray-1100" icon="CircleXmarkSolid" />
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    );
  },
);
